import { GameConsole } from "./console/gameConsole";
import { pictureCover, SMOKE_FIRES, SMOKE_PICTURE, SMOKE_WIND, smokeMayMove, SmokePacer } from "./menuSmokeLogic";
import { softwareRendering } from "./utils/graphicsAcceleration";

/*
    The menu's smoke, drifting (the owner, 17 October 2026: "Can the smoke be animated?"). The menu's background is a
    still picture of Fargo at dusk (MENU_BACKGROUND, smoke and all: docs/ASSET_REPLACEMENT.md). Where the browser can
    afford it, the same picture without its smoke (MENU_BACKGROUND_CLEAR) goes behind the menu and the smoke is drawn
    over it live: the plumes of its five fires blown north-east by the wind, the light of the flames flickering, sparks
    going up. The still picture is this shader's first frame laid over the clear one, so the switch doesn't show.

    It isn't drawn when the player asked for reduced motion, when the Battlefield look is off or Low (the lightest
    graphics), without a graphics card (`softwareRendering`), without WebGL or precise floats in it, while the menu is
    hidden (a match is on) or the tab is; and it gives up for the visit on a device that can't keep it smooth. It is
    drawn small (half the screen's pixels or fewer: smoke is soft) and at most 30 times a second. Its rules (where the
    picture lies, when it may move, when a frame is drawn) are in menuSmokeLogic.ts.
*/

/** the menu's background: the still picture, smoke and all */
export const MENU_BACKGROUND = "./img/backgrounds/fargo_menu.webp";
/** the same picture without the smoke, under the live smoke */
export const MENU_BACKGROUND_CLEAR = "./img/backgrounds/fargo_menu_clear.webp";

const VERTEX = /* glsl */`
attribute vec2 aPos;
void main() { gl_Position = vec4(aPos, 0.0, 1.0); }
`;

const FRAGMENT = /* glsl */`
precision highp float;
uniform vec2 uBuf;
uniform vec2 uOrigin;
uniform float uScale;
uniform float uTime;
uniform vec4 uFire[5];
uniform vec4 uFire2[5];
uniform vec2 uWind;

float hash(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
}
float vnoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}
const mat2 TURN = mat2(1.6, 1.2, -1.2, 1.6);
float fbm5(vec2 p) {
    float s = 0.0;
    float a = 0.5;
    for (int i = 0; i < 5; i++) { s += a * vnoise(p); p = TURN * p + 7.31; a *= 0.5; }
    return s / 0.96875;
}
float fbm3(vec2 p) {
    float s = 0.0;
    float a = 0.5;
    for (int i = 0; i < 3; i++) { s += a * vnoise(p); p = TURN * p + 3.17; a *= 0.5; }
    return s / 0.875;
}

void main() {
    // this pixel's place in the picture (the canvas covers the menu as its background does)
    vec2 p = uOrigin + vec2(gl_FragCoord.x, uBuf.y - gl_FragCoord.y) * uScale;
    vec2 perp = vec2(-uWind.y, uWind.x);

    // most of the screen has no smoke on it: nothing to work out there
    bool near = false;
    for (int i = 0; i < 5; i++) {
        vec2 d = p - uFire[i].xy;
        float size = uFire[i].z;
        float s = dot(d, uWind);
        float t = dot(d, perp);
        // (the plume's own width, plus how far its billowing edge can stray; beyond 2.6 times that, nothing shows)
        float wide = 22.0 * size + max(s, 0.0) * 0.25 + 70.0 * size;
        if ((s > -200.0 * size && s < uFire2[i].y * size * 2.6 && abs(t) < wide * 2.6) || dot(d, d) < 360000.0 * size * size) near = true;
    }
    if (!near) { gl_FragColor = vec4(0.0); return; }

    // the smoke's texture flows away from the fires with the wind; its edges billow
    vec2 flow = uWind * uTime;
    float billow = fbm5((p - flow * 26.0) / 90.0);
    float fine = fbm5((p - flow * 42.0) / 34.0 + 17.0);
    vec2 warp = vec2(fbm3((p - flow * 18.0) / 160.0 + 3.0), fbm3((p - flow * 18.0) / 160.0 + 11.0)) - 0.5;

    float smoke = 0.0;
    float shade = 0.0;
    float clearOfFlames = 1.0;
    vec3 light = vec3(0.0);
    vec3 glow = vec3(0.0);
    float sparks = 0.0;
    for (int i = 0; i < 5; i++) {
        vec2 at = uFire[i].xy;
        float size = uFire[i].z;
        float heat = uFire2[i].x;
        vec2 d = p + warp * 130.0 * size - at;
        float s = dot(d, uWind);
        float t = dot(d, perp) + sin(s / (210.0 * size) - uTime * 0.35) * 26.0 * size * smoothstep(0.0, 300.0 * size, s);
        float sigma = 22.0 * size + max(s, 0.0) * 0.25;
        float along = smoothstep(-14.0 * size, 50.0 * size, s) * exp(-max(s, 0.0) / (uFire2[i].y * size));
        float across = exp(-(t * t) / (2.0 * sigma * sigma));
        float dens = across * along * (0.45 + 0.9 * billow) * (0.8 + 0.4 * fine);
        dens = smoothstep(0.06, 0.7, dens) * uFire[i].w;
        smoke = 1.0 - (1.0 - smoke) * (1.0 - dens);
        shade = max(shade, dens * smoothstep(-sigma, sigma, t));

        vec2 r = p - at;
        float r2 = dot(r, r) / (size * size);
        // the flames stay in sight: the smoke starts where they end
        clearOfFlames = min(clearOfFlames, smoothstep(0.75, 1.6, sqrt(r2) / 36.0));
        light += heat * (exp(-r2 / 16200.0) * vec3(0.95, 0.45, 0.12) * 0.40 + exp(-r2 / 156800.0) * vec3(0.6, 0.27, 0.08) * 0.13);
        // the flames' light flickers
        float flicker = vnoise(vec2(uTime * 7.0, float(i) * 13.0)) * 0.6 + vnoise(vec2(uTime * 17.0, float(i) * 5.0 + 40.0)) * 0.4;
        glow += heat * flicker * exp(-r2 / 9800.0) * vec3(0.95, 0.42, 0.1) * 0.2;
        // sparks going up with the smoke (none further than 520 pixels, times the fire's size)
        if (r2 > 270400.0) continue;
        for (int k = 0; k < 8; k++) {
            float fk = float(k);
            float seed = hash(vec2(fk * 3.7 + 1.0, float(i) * 7.0 + 3.0));
            float life = 3.0 + 2.5 * seed;
            float ph = fract(uTime / life + seed * 1.7);
            float travel = ph * (220.0 + 260.0 * seed) * size;
            vec2 spark = at + uWind * travel + perp * (sin(ph * 6.2831 * (1.0 + seed) + fk) * 16.0 * size + (seed - 0.5) * 46.0 * size);
            float dist = length(p - spark);
            float bright = (1.0 - ph) * (1.0 - ph) * smoothstep(0.0, 0.06, ph);
            sparks += bright * exp(-dist * dist / 12.0);
        }
    }

    // over the picture as its grade would have it: the smoke is darkened at the top (behind the logo), at the edges and
    // a little in the middle, as the ground is
    vec2 uv = p / vec2(${SMOKE_PICTURE.width}.0, ${SMOKE_PICTURE.height}.0);
    float band = fbm3(p / 380.0 + 3.7);
    float band2 = fbm3(p / 150.0 + 9.1);
    float top = (1.0 - smoothstep(0.0, 0.26, uv.y + (band - 0.5) * 0.16)) * (0.6 + 0.5 * band2);
    float post = 1.0 - clamp(top, 0.0, 1.0) * 0.55;
    vec2 lq = vec2((uv.x - 0.5) / 0.2, (uv.y - 0.1) / 0.12);
    post *= 1.0 - 0.42 * exp(-dot(lq, lq));
    vec2 vq = vec2((uv.x - 0.5) / 0.7, (uv.y - 0.5) / 0.62);
    post *= 1.0 - smoothstep(0.45, 1.25, length(vq)) * 0.66;
    vec2 cq = vec2((uv.x - 0.5) / 0.2, (uv.y - 0.6) / 0.55);
    post *= 1.0 - 0.16 * exp(-dot(cq, cq));

    vec3 dark = vec3(0.05, 0.047, 0.043);
    vec3 grey = vec3(0.24, 0.235, 0.22);
    vec3 colour = dark * (0.6 + 0.4 * shade) + grey * (1.0 - shade) * (0.2 + 0.8 * billow) + light * (1.0 - shade) * 0.55;
    float alpha = clamp(smoke * 0.9, 0.0, 0.93) * clearOfFlames;
    // a little grain, as the picture has
    float grain = (hash(floor(gl_FragCoord.xy) + 0.5) - 0.5) * 0.02;
    vec3 lit = (glow + vec3(1.0, 0.72, 0.36) * min(sparks, 1.5) * 0.9) * post;
    gl_FragColor = vec4(max((colour + grain) * post * alpha + lit, 0.0), alpha);
}
`;

export interface SmokeRenderer {
    /** draws the smoke at `time` seconds, for a box of the given size (CSS pixels), `scale` canvas pixels a CSS pixel */
    draw(time: number, width: number, height: number, scale: number): void
    destroy(): void
}

/** the smoke drawn into a canvas of its own, or nothing where WebGL (with precise floats) isn't to be had */
export function createSmokeRenderer(canvas: HTMLCanvasElement, preserveDrawingBuffer = false): SmokeRenderer | undefined {
    const gl = canvas.getContext("webgl", { alpha: true, premultipliedAlpha: true, antialias: false, depth: false, stencil: false, preserveDrawingBuffer, powerPreference: "low-power" });
    if (!gl) return undefined;
    const high = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
    if (!high || high.precision < 16) return undefined;

    const compile = (type: number, source: string): WebGLShader | undefined => {
        const shader = gl.createShader(type);
        if (!shader) return undefined;
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            console.warn("The menu's smoke didn't compile:", gl.getShaderInfoLog(shader));
            gl.deleteShader(shader);
            return undefined;
        }
        return shader;
    };
    const vertex = compile(gl.VERTEX_SHADER, VERTEX);
    const fragment = compile(gl.FRAGMENT_SHADER, FRAGMENT);
    const program = gl.createProgram();
    if (!vertex || !fragment || !program) return undefined;
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        console.warn("The menu's smoke didn't link:", gl.getProgramInfoLog(program));
        return undefined;
    }
    gl.useProgram(program);

    // one triangle over the whole canvas
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, "aPos");
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const uniform = (name: string): WebGLUniformLocation | null => gl.getUniformLocation(program, name);
    const uBuf = uniform("uBuf"), uOrigin = uniform("uOrigin"), uScale = uniform("uScale"), uTime = uniform("uTime");
    gl.uniform4fv(uniform("uFire"), new Float32Array(SMOKE_FIRES.flatMap(fire => [fire.x, fire.y, fire.size, fire.smoke])));
    gl.uniform4fv(uniform("uFire2"), new Float32Array(SMOKE_FIRES.flatMap(fire => [fire.heat, fire.reach, 0, 0])));
    gl.uniform2f(uniform("uWind"), SMOKE_WIND[0], SMOKE_WIND[1]);
    gl.disable(gl.BLEND);

    return {
        draw(time, width, height, scale) {
            const bufferWidth = Math.max(1, Math.round(width * scale));
            const bufferHeight = Math.max(1, Math.round(height * scale));
            if (canvas.width !== bufferWidth) canvas.width = bufferWidth;
            if (canvas.height !== bufferHeight) canvas.height = bufferHeight;
            // the canvas covers exactly the box the background does (the menu's, which a scrollbar can narrow)
            const cssWidth = `${width}px`, cssHeight = `${height}px`;
            if (canvas.style.width !== cssWidth) canvas.style.width = cssWidth;
            if (canvas.style.height !== cssHeight) canvas.style.height = cssHeight;
            gl.viewport(0, 0, bufferWidth, bufferHeight);
            // where the picture lies, as the menu's background lays it out
            const { originX, originY, scale: perPixel } = pictureCover(width, height, bufferWidth);
            gl.uniform2f(uBuf, bufferWidth, bufferHeight);
            gl.uniform2f(uOrigin, originX, originY);
            gl.uniform1f(uScale, perPixel);
            gl.uniform1f(uTime, time);
            gl.clearColor(0, 0, 0, 0);
            gl.clear(gl.COLOR_BUFFER_BIT);
            gl.drawArrays(gl.TRIANGLES, 0, 3);
        },
        destroy() {
            gl.deleteBuffer(buffer);
            gl.deleteProgram(program);
            gl.deleteShader(vertex);
            gl.deleteShader(fragment);
            gl.getExtension("WEBGL_lose_context")?.loseContext();
        }
    };
}

/** whether the smoke may move now: the player's own settings and the browser */
function smokeAllowed(): boolean {
    return smokeMayMove({
        reducedMotion: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false,
        look: String(GameConsole.getBuiltInCVar("cv_war_look") ?? "high"),
        software: softwareRendering()
    });
}

/** canvas pixels a CSS pixel: a phone's smaller */
const SCALE = (): number => (window.matchMedia?.("(pointer: coarse)").matches ? 0.4 : 0.5);

let started = false;

/**
 * Starts the menu's smoke over `splash` (the menu, whose background is MENU_BACKGROUND) where it may move; watches the
 * player's settings and the menu after that, and stops and starts it with them
 */
export function startMenuSmoke(splash: HTMLElement): void {
    if (started) return;
    started = true;

    let canvas: HTMLCanvasElement | undefined;
    let renderer: SmokeRenderer | undefined;
    let pacer = new SmokePacer();
    let frame = 0;
    let gaveUp = false;
    let starting = false;

    const setBackground = (url: string): void => {
        splash.style.backgroundImage = `url(${url})`;
    };

    const stop = (): void => {
        cancelAnimationFrame(frame);
        frame = 0;
        if (canvas || renderer) setBackground(MENU_BACKGROUND);
        renderer?.destroy();
        renderer = undefined;
        canvas?.remove();
        canvas = undefined;
    };

    const tick = (now: number): void => {
        frame = requestAnimationFrame(tick);
        if (!renderer) return;
        // (hidden: a match is on, or the tab is)
        const step = pacer.frame(now, splash.clientHeight !== 0 && !document.hidden);
        if (step.kind === "stop") {
            console.info("The menu's smoke stops: this device can't draw it smoothly.");
            gaveUp = true;
            stop();
        } else if (step.kind === "draw") {
            renderer.draw(step.time, splash.clientWidth, splash.clientHeight, SCALE());
        }
    };

    const start = (): void => {
        if (renderer || starting || gaveUp || !smokeAllowed()) return;
        starting = true;
        const next = document.createElement("canvas");
        next.id = "splash-smoke";
        next.setAttribute("aria-hidden", "true");
        const made = createSmokeRenderer(next);
        if (!made) {
            // no WebGL to be had: the still picture stays, for good
            gaveUp = true;
            starting = false;
            return;
        }
        // the clear picture loaded first, so the smoke and it come in the same frame
        const clear = new Image();
        clear.src = MENU_BACKGROUND_CLEAR;
        clear.decode().then(() => {
            starting = false;
            if (!smokeAllowed() || gaveUp) {
                made.destroy();
                return;
            }
            canvas = next;
            renderer = made;
            pacer = new SmokePacer();
            splash.prepend(canvas);
            // the first frame (the still picture's) and the clear picture come together
            frame = requestAnimationFrame(now => {
                pacer.frame(now, true);
                renderer?.draw(0, splash.clientWidth, splash.clientHeight, SCALE());
                setBackground(MENU_BACKGROUND_CLEAR);
                frame = requestAnimationFrame(tick);
            });
        }).catch(() => {
            starting = false;
            made.destroy();
        });
    };

    start();
    // the player's settings can change it (the look in Settings, reduced motion in the system)
    window.setInterval(() => {
        if (renderer && !smokeAllowed()) stop();
        else if (!renderer) start();
    }, 1000);
}
